/**
 * Text Escape/Unescape Service
 * Implements business logic for text escaping operations
 * Supports HTML entities, JavaScript/JSON, and URL encoding
 */

import { EncodingOperation } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type EscapeType = 'html' | 'javascript' | 'url';

export class TextEscapeService {
  /**
   * Escape text based on escape type
   */
  static async escape(
    text: string,
    escapeType: EscapeType = 'html'
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules('text-escape' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'text-escape' as any, validation.suggestion);
      }

      // Perform escaping
      const escapedText = this.escapeText(text, escapeType);

      return {
        id: this.generateOperationId(),
        toolType: 'text-escape' as any,
        operationType: 'escape',
        inputData: text,
        outputData: escapedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          escapeType,
          originalLength: text.length,
          escapedLength: escapedText.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to escape text',
        'text-escape' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Unescape text based on escape type
   */
  static async unescape(
    text: string,
    escapeType: EscapeType = 'html'
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules('text-escape' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'text-escape' as any, validation.suggestion);
      }

      // Perform unescaping
      const unescapedText = this.unescapeText(text, escapeType);

      return {
        id: this.generateOperationId(),
        toolType: 'text-escape' as any,
        operationType: 'unescape',
        inputData: text,
        outputData: unescapedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          escapeType,
          originalLength: text.length,
          unescapedLength: unescapedText.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to unescape text',
        'text-escape' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Core text escaping implementation
   */
  private static escapeText(text: string, escapeType: EscapeType): string {
    switch (escapeType) {
      case 'html':
        return this.escapeHtml(text);
      case 'javascript':
        return this.escapeJavaScript(text);
      case 'url':
        return this.escapeUrl(text);
      default:
        return text;
    }
  }

  /**
   * Core text unescaping implementation
   */
  private static unescapeText(text: string, escapeType: EscapeType): string {
    try {
      switch (escapeType) {
        case 'html':
          return this.unescapeHtml(text);
        case 'javascript':
          return this.unescapeJavaScript(text);
        case 'url':
          return this.unescapeUrl(text);
        default:
          return text;
      }
    } catch (error) {
      throw new ValidationError(
        'Failed to unescape text',
        'text-escape' as any,
        'The input may be corrupted or in an unexpected format'
      );
    }
  }

  /**
   * HTML entity escaping
   */
  private static escapeHtml(text: string): string {
    const htmlEntities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;'
    };

    return text.replace(/[&<>"'/]/g, (char) => htmlEntities[char] || char);
  }

  /**
   * HTML entity unescaping
   */
  private static unescapeHtml(text: string): string {
    const htmlEntities: Record<string, string> = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&#x2F;': '/',
      '&apos;': "'"
    };

    return text.replace(/&[^;]+;/g, (entity) => htmlEntities[entity] || entity);
  }

  /**
   * JavaScript/JSON escaping
   */
  private static escapeJavaScript(text: string): string {
    const jsEscapes: Record<string, string> = {
      '\\': '\\\\',
      '"': '\\"',
      "'": "\\'",
      '\n': '\\n',
      '\r': '\\r',
      '\t': '\\t',
      '\b': '\\b',
      '\f': '\\f'
    };

    return text.replace(/[\\"\'\n\r\t\b\f]/g, (char) => jsEscapes[char] || char);
  }

  /**
   * JavaScript/JSON unescaping
   */
  private static unescapeJavaScript(text: string): string {
    const jsUnescapes: Record<string, string> = {
      '\\\\': '\\',
      '\\"': '"',
      "\\'": "'",
      '\\n': '\n',
      '\\r': '\r',
      '\\t': '\t',
      '\\b': '\b',
      '\\f': '\f'
    };

    let result = text;
    for (const [escaped, unescaped] of Object.entries(jsUnescapes)) {
      result = result.replace(new RegExp(escaped.replace(/\\/g, '\\\\'), 'g'), unescaped);
    }
    return result;
  }

  /**
   * URL encoding
   */
  private static escapeUrl(text: string): string {
    return encodeURIComponent(text);
  }

  /**
   * URL decoding
   */
  private static unescapeUrl(text: string): string {
    return decodeURIComponent(text);
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `text-escape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get escape type info for display
   */
  static getEscapeTypeInfo(escapeType: EscapeType): { name: string; description: string } {
    switch (escapeType) {
      case 'html':
        return {
          name: 'HTML Entities',
          description: 'Escape special HTML characters like < > & " \''
        };
      case 'javascript':
        return {
          name: 'JavaScript/JSON',
          description: 'Escape quotes, newlines, and special characters'
        };
      case 'url':
        return {
          name: 'URL Encoding',
          description: 'Percent-encoding for URL components'
        };
      default:
        return {
          name: 'HTML Entities',
          description: 'Escape special HTML characters'
        };
    }
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
